import { demoToday, shiftDays } from '../../../../lib/demoTime';

export type CouponKind = 'ordinary' | 'recommendation';
export type CouponStatus = '进行中' | '未开启' | '已结束';
export type StackMode = 'none' | 'global' | 'specified';

export type UseTime =
  | { readonly type: 'range'; readonly end: string; readonly start: string }
  | { readonly type: 'relative'; readonly days: number };

export type IssueTime =
  | { readonly type: 'range'; readonly end: string; readonly start: string }
  | { readonly type: 'unlimited' };

/*
 * 种子的窗口写成「相对今天偏移 N 天」，在构造时才落到具体日期。因此无论哪天打开，
 * 数据的相对位置都一样：不会整批过期，状态分布也恒定。这是 mock 数据与真实时间的解耦点。
 */

export interface Coupon {
  readonly id: number;
  readonly name: string;
  readonly source: string;
  readonly kind: CouponKind;
  readonly amount: string;
  readonly type: string;
  readonly threshold: string;
  readonly issued?: number;
  readonly sent?: number;
  readonly used?: number;
  readonly useTime: UseTime;
  readonly issueTime: IssueTime;
  readonly enabled: boolean;
  /** 关闭时不纳入其他优惠券的全局叠加范围；用于模拟尚未配置叠加关系的优惠券。 */
  readonly globalStackingEligible?: boolean;
  readonly stackMode: StackMode;
  readonly specified: readonly number[];
}

export interface CouponGroup {
  readonly id: number;
  readonly name: string;
  readonly couponIds: readonly number[];
}

/* 种子里的窗口用「相对今天的天数」表达，而不是写死的日期字符串。 */
export type IssueSpec = { readonly type: 'unlimited' } | { readonly from: number; readonly to: number; readonly type: 'range' };
export type UseSpec = { readonly days: number; readonly type: 'relative' } | { readonly from: number; readonly to: number; readonly type: 'range' };

export type CouponSeed = Omit<Coupon, 'issueTime' | 'useTime'> & {
  readonly issueTime: IssueSpec;
  readonly useTime: UseSpec;
};

const unlimited: IssueSpec = { type: 'unlimited' };

/** 发放窗口：相对今天第 from 天到第 to 天。 */
function issueWindow(from: number, to: number): IssueSpec {
  return { from, to, type: 'range' };
}

/** 使用窗口同一形态；跨 today 即为进行中，落在未来即未开启，全部落在过去即已结束。 */
function validWindow(from: number, to: number): UseSpec {
  return { from, to, type: 'range' };
}

/** 领券当日起 N 天：与时间无关，永远可用。 */
function validDays(days: number): UseSpec {
  return { days, type: 'relative' };
}

function resolveIssue(spec: IssueSpec, today: string): IssueTime {
  return spec.type === 'unlimited' ? { type: 'unlimited' } : { end: shiftDays(today, spec.to), start: shiftDays(today, spec.from), type: 'range' };
}

function resolveUse(spec: UseSpec, today: string): UseTime {
  return spec.type === 'relative' ? { days: spec.days, type: 'relative' } : { end: shiftDays(today, spec.to), start: shiftDays(today, spec.from), type: 'range' };
}

/**
 * 状态是**派生值**，不是存储字段——日期是输入，状态是输出。
 * 优先级：发放期 → 使用期 → 启用开关。启用开关只兜底「不限时间」的券，
 * 这样「发放期已过且已停用」的券仍显示已结束，不会被停用标志盖掉。
 */
export function deriveCouponStatus(coupon: Coupon, today: string = demoToday): CouponStatus {
  if (coupon.issueTime.type === 'range') {
    if (coupon.issueTime.start > today) return '未开启';
    if (coupon.issueTime.end < today) return '已结束';
  }
  if (coupon.useTime.type === 'range') {
    if (coupon.useTime.start > today) return '未开启';
    if (coupon.useTime.end < today) return '已结束';
  }
  return coupon.enabled ? '进行中' : '未开启';
}

export function buildCoupons(seeds: readonly CouponSeed[], today: string = demoToday): readonly Coupon[] {
  return seeds.map(({ issueTime, useTime, ...rest }) => ({
    ...rest,
    issueTime: resolveIssue(issueTime, today),
    useTime: resolveUse(useTime, today),
  }));
}

export function formatUseTime(useTime: UseTime): string {
  return useTime.type === 'relative' ? `领券当日起${useTime.days}天` : `${useTime.start} ~ ${useTime.end}`;
}

export function formatIssueTime(issueTime: IssueTime): string {
  return issueTime.type === 'unlimited' ? '不限' : `${issueTime.start} ~ ${issueTime.end}`;
}

/*
 * 17 条按三种状态编排，每种状态的窗口位置决定了它的状态：
 *   进行中 — 发放不限或跨越今天，且有效使用期未过期
 *   未开启 — 发放期或使用期整体落在未来
 *   已结束 — 发放期整体落在过去
 * 因为都是相对量，2026 年打开和 2028 年打开看到的分布完全一致。
 */
export const couponSeeds: readonly CouponSeed[] = [
  { id: 17, name: '新客体验券', source: '优惠券', kind: 'ordinary', amount: '100.00', type: '课时券', threshold: '无门槛', issued: 0, sent: 0, used: 0, useTime: validDays(30), issueTime: unlimited, enabled: false, globalStackingEligible: false, stackMode: 'none', specified: [] },
  { id: 1, name: '满1000减20', source: '优惠券', kind: 'ordinary', amount: '20.00', type: '满减券', threshold: '满1000元可用', issued: 1000, sent: 438, used: 126, useTime: validDays(100), issueTime: unlimited, enabled: true, stackMode: 'specified', specified: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 101, 102] },
  { id: 2, name: '语文报名优惠券', source: '优惠券', kind: 'ordinary', amount: '90.00', type: '折扣券', threshold: '满500元可用', issued: 3000, sent: 826, used: 319, useTime: validDays(30), issueTime: unlimited, enabled: true, stackMode: 'specified', specified: [3, 4] },
  { id: 3, name: '数学报名优惠券', source: '优惠券', kind: 'ordinary', amount: '100.00', type: '满减券', threshold: '满1000元可用', issued: 3000, sent: 753, used: 286, useTime: validDays(30), issueTime: unlimited, enabled: true, stackMode: 'none', specified: [] },
  { id: 4, name: '老带新报名券', source: '优惠券', kind: 'ordinary', amount: '200.00', type: '满减券', threshold: '无门槛', issued: 10048, sent: 56, used: 7, useTime: validDays(30), issueTime: unlimited, enabled: true, stackMode: 'none', specified: [] },
  { id: 5, name: '体验课赠2课时', source: '优惠券', kind: 'ordinary', amount: '200.00', type: '课时券', threshold: '无门槛', issued: 1000, sent: 297, used: 95, useTime: validDays(60), issueTime: unlimited, enabled: true, stackMode: 'none', specified: [] },
  { id: 6, name: '春季大放送', source: '优惠券', kind: 'ordinary', amount: '300.00', type: '满减券', threshold: '满2000元可用', issued: 1011, sent: 266, used: 98, useTime: validWindow(-188, -70), issueTime: issueWindow(-218, -188), enabled: false, stackMode: 'specified', specified: [3, 4, 5, 7] },
  { id: 7, name: '暑期课程85折', source: '优惠券', kind: 'ordinary', amount: '85.00', type: '折扣券', threshold: '满2科可用', issued: 2000, sent: 0, used: 0, useTime: validWindow(12, 102), issueTime: unlimited, enabled: false, stackMode: 'none', specified: [] },
  { id: 8, name: '英语秋季班优惠券', source: '优惠券', kind: 'ordinary', amount: '50.00', type: '满减券', threshold: '满500元可用', issued: 500, sent: 87, used: 32, useTime: validDays(45), issueTime: unlimited, enabled: true, stackMode: 'none', specified: [] },
  { id: 9, name: '双十一秒杀券', source: '优惠券', kind: 'ordinary', amount: '30.00', type: '满减券', threshold: '满300元可用', issued: 800, sent: 120, used: 45, useTime: validDays(15), issueTime: issueWindow(22, 32), enabled: true, stackMode: 'specified', specified: [2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 13] },
  { id: 10, name: '新生体验课券', source: '优惠券', kind: 'ordinary', amount: '100.00', type: '课时券', threshold: '无门槛', issued: 1500, sent: 340, used: 110, useTime: validDays(30), issueTime: unlimited, enabled: true, stackMode: 'none', specified: [] },
  { id: 11, name: '春节限时券', source: '优惠券', kind: 'ordinary', amount: '150.00', type: '满减券', threshold: '满1500元可用', issued: 600, sent: 0, used: 0, useTime: validWindow(145, 163), issueTime: issueWindow(140, 158), enabled: false, stackMode: 'none', specified: [] },
  { id: 12, name: '会员专享9折券', source: '优惠券', kind: 'ordinary', amount: '90.00', type: '折扣券', threshold: '无门槛', issued: 400, sent: 95, used: 40, useTime: validDays(90), issueTime: unlimited, enabled: true, stackMode: 'none', specified: [] },
  { id: 13, name: '暑期续费赠课时', source: '优惠券', kind: 'ordinary', amount: '300.00', type: '课时券', threshold: '满2科可用', issued: 900, sent: 156, used: 61, useTime: validDays(60), issueTime: issueWindow(-45, 45), enabled: true, stackMode: 'none', specified: [] },
  { id: 14, name: '寒假集训营立减券', source: '优惠券', kind: 'ordinary', amount: '500.00', type: '满减券', threshold: '满3000元可用', issued: 300, sent: 0, used: 0, useTime: validWindow(72, 161), issueTime: unlimited, enabled: false, stackMode: 'specified', specified: [15] },
  { id: 15, name: '老带新秋季狂欢券', source: '优惠券', kind: 'ordinary', amount: '80.00', type: '折扣券', threshold: '满800元可用', issued: 700, sent: 210, used: 88, useTime: validDays(30), issueTime: issueWindow(-20, 25), enabled: true, stackMode: 'none', specified: [] },
  { id: 16, name: '秋季体验课券', source: '优惠券', kind: 'ordinary', amount: '100.00', type: '课时券', threshold: '无门槛', issued: 1200, sent: 640, used: 352, useTime: validWindow(-19, 41), issueTime: issueWindow(-36, -6), enabled: false, stackMode: 'none', specified: [] },
];

export const couponFixtures: readonly Coupon[] = buildCoupons(couponSeeds);

export const couponGroupFixtures: readonly CouponGroup[] = [
  { id: 201, name: '报名组合一', couponIds: [3, 4] },
  { id: 202, name: '体验组合二', couponIds: [5, 7] },
  { id: 203, name: '秋季组合三', couponIds: [8, 10] },
  { id: 204, name: '会员组合四', couponIds: [11, 12] },
  { id: 205, name: '续费组合五', couponIds: [13, 3] },
];
